/**
 * @truckbuddy/contract — the API surface, shared by apps/api and apps/web.
 *
 * **This package must stay browser-safe.** It may not import `@truckbuddy/db`,
 * `@truckbuddy/integrations`, `@truckbuddy/monitoring-db`, `postgres`,
 * `drizzle-orm`, or anything from `node:`. That boundary is the whole point:
 * `packages/integrations` holds vendor bearer tokens and `packages/db` holds the
 * Postgres URL, and neither may ever be reachable from a browser bundle. The web
 * app imports types from here and data over HTTP — never a query.
 *
 * `packages/contract/src/boundary.test.ts` enforces this by reading the sources,
 * so a stray import fails the build rather than shipping.
 */
export * from "./vocabulary.js";
export * from "./wire.js";
export * from "./requests.js";
// The work-order queue's column order (TRBUD-391): the keys, the click-order
// layering rule, the URL spelling and the group headings. Browser-safe, pure.
export * from "./queue-sort.js";
export * from "./permissions.js";
export * from "./pipeline-edit.js";
export * from "./money.js";
export * from "./field-shapes.js";
export * from "./reports.js";
export * from "./dates.js";
export * from "./stage-time.js";
export * from "./categories.js";
export * from "./auth.js";
export * from "./labour-rates.js";
export * from "./asset-lifecycle.js";
export * from "./navigation.js";
export * from "./availability.js";
// The cadence table and the freshness classifier. Browser-safe: data and
// arithmetic only. The Bicep parser is deliberately NOT re-exported here — it is
// server/tooling-side and `apps/web` has no business importing it.
export * from "./collector-schedule.js";
export * from "./distance.js";
// Live asset positions (TRBUD-55) — the five-minute TTL, the three marker states
// and the map's wire shapes. Browser-safe: types, one constant and arithmetic.
export * from "./positions.js";
// The two tabular reports the Reports screen renders itself: spend by
// work-order category with its de-duplicated footer (TRBUD-154) and
// purchase-order-versus-invoice variance (TRBUD-141). Types only.
export * from "./spend-reports.js";
export * from "./cash-forecast.js";
// Where each stage requirement is actually entered (TRBUD-156). A routing table
// and its wire shapes — it holds no opinion about whether a requirement is
// satisfied, which stays `evaluateRequiredFields`'s alone.
export * from "./requirement-entry.js";
// The Sylectus load-status snapshot stamped on a work order at creation
// (DEC-01). Browser-safe: two vocabularies, one TTL constant, and pure
// formatters. It reads the dispatch axis and writes nothing on it.
export * from "./load-status.js";

// The two downtime numbers, named once (TRBUD-222 / DEC-03): asset downtime is
// GROSS and never reduced by a stoppage, active repair time is NET. Strings only.
export * from "./downtime-labels.js";

// What "Spend YTD" and "Lifetime cost" count, said once (TRBUD-334, TRBUD-382).
// Strings only.
export * from "./spend-labels.js";

// What a receipt parser proposed for an invoice or receipt, and the one 80%
// review threshold (30 September 2026). Types, a constant and arithmetic.
export * from "./receipt-parser.js";
// The Review queue's comparison rows, PO line matching and queue tabs. Pure.
export * from "./receipt-review.js";
// The files on a purchase order and where each stands in that queue (TRBUD-351).
export * from "./purchase-order-documents.js";
// A receipt that runs over several files (TRBUD-352): the page and join shapes,
// the invoice-number rule, automatic joining and the combined AI proposal. Pure.
export * from "./receipt-continuation.js";
// One Review item per bill when a file holds several (TRBUD-318 item B): the
// bill keys, which bills a read splits, the add-a-bill rule and the wire shapes.
export * from "./receipt-bills.js";
// Parts handed to a mechanic and his applying them (TRBUD-416): request bodies
// and wire shapes only.
export * from "./part-custody.js";
// Terminal spare-stock rules and their coverage (TRBUD-406): request bodies,
// wire shapes and two pure rules.
export * from "./spare-stock.js";
// What a vendor supplied, line by line (TRBUD-418): the query, the wire shapes
// and the report-builder definition its Export sends.
export * from "./vendor-supplied-lines.js";
// How we use a vendor (TRBUD-466 Part 2): the insights, item and compare queries,
// their wire shapes and the report-builder definition the panel's Export sends.
export * from "./vendor-insights.js";

/** Header the dev sign-in picker uses to choose a persona. */
export const PERSONA_HEADER = "x-truckbuddy-persona";

/**
 * The two halves of the CSRF double-submit, named once so they cannot drift.
 *
 * `apps/api` sets the cookie and compares the header; `apps/web` reads the
 * cookie and sends the header. Before UAT 0908 finding 14 only the first half of
 * each pair existed, and the cookie name was spelled in exactly one place
 * (`routes/auth.ts`) — which was enough while nobody else needed it and is not
 * now that two packages do.
 *
 * The COOKIE is deliberately readable by scripts, unlike `tb_session`: that
 * asymmetry IS the mechanism. A cross-site attacker can make the browser SEND
 * cookies but cannot READ them, so requiring the value in a header proves the
 * request was built by a page on this origin.
 */
export const CSRF_COOKIE = "tb_csrf";
export const CSRF_HEADER = "x-csrf-token";

/**
 * Display id for a work order, from its generated `number`.
 *
 * ── Accepts a string, because that is what it has always been HANDED ──
 * Widened 7 September 2026. `work_order.number` is a `bigint`, and postgres.js
 * returns int8 as a STRING — `createSql` sets `types: {}`, so nothing parses it.
 * Every read path that selects `w.number` without a `::text` therefore passes
 * this function a string while declaring a number, so the old `number`-only
 * signature was not a constraint being enforced; it was part of the same
 * mistyping, and it made the honest fix (`::text` + `string | null`) look like a
 * type error at the call site.
 *
 * The rendered value is identical either way — a template literal stringifies a
 * number to exactly the digits Postgres sent — so this changes no output. What it
 * changes is that a read path can now be typed truthfully without having to
 * launder a bigint back through `Number()`, which is the one thing that would
 * actually corrupt a value beyond 2^53.
 *
 * `parseWorkOrderDisplayId` still returns `number` and is unchanged: it parses
 * ids that live in URLs and search boxes, where the value is small by
 * construction and callers do arithmetic on it.
 *
 * ── The one formatter, and it never pads ──
 * Since TRBUD-358 (8 October 2026) new work orders start at 1,051,051 so a work
 * order cannot be read as a four-digit unit number, which means `WO-829`,
 * `WO-1438` and `WO-1051051` all appear in one list. The label is `WO-` and the
 * digits, nothing else. Do not build it by hand and do not pad it: a `padStart`
 * of six in the documents route was the one place that printed it differently.
 * SQL that needs the label inside a query uses `workOrderDisplayIdSql` in
 * `apps/api/src/read/work-order-display-id.ts`, and
 * `work-order-display-id-usage.test.ts` fails the gate on any other spelling.
 */
export function workOrderDisplayId(number: number | string): string {
  return `WO-${number}`;
}

/** Parse a display id back to a number, or null if it is not one. */
export function parseWorkOrderDisplayId(displayId: string): number | null {
  const match = /^WO-(\d+)$/.exec(displayId.trim().toUpperCase());
  return match?.[1] ? Number(match[1]) : null;
}
export * from "./kanban.js";
export * from "./part-summary.js";
// Which photo a list row shows and who may draw its thumbnail (TRBUD-373).
export * from "./list-photos.js";
// Maintenance mode (SigmaBuddy's SIGM-205, adapted): the state resolver the API's
// gate and the splash page share, the fleet-zone time helpers and the two
// request bodies. Browser-safe: arithmetic and `Intl`.
export * from "./site-maintenance.js";
// TRBUD-448: deleting an unused fault type or location from Configuration —
// the body, the deletion-check shapes and the name of each kind of reference.
export * from "./config-delete.js";
// How a repair-history row reads beside its unit: the unit number a MaintainX
// title starts with is dropped where the unit is already named. Display only.
export * from "./history-display.js";

export * from "./mainx-expense-review.js";
export * from "./report-matrix.js";

export * from "./report-matrix-registry.js";
export * from "./report-matrix-builtins.js";
// TRBUD-447: the records behind a saved, pinned or builder report figure.
export * from "./report-matrix-drill.js";
export * from "./asset-document-types.js";
export * from "./purchase-payment.js";
// Invoice value by recorded payment method: the list, summary and CSV query and
// the summary's wire shape. Browser-safe: zod and types.
export * from "./purchase-payment-report.js";
// Recording a DOT inspection on an asset and voiding one (TRBUD-342): the two
// request bodies and the panel's wire shapes. Browser-safe: zod and types.
export * from "./inspections.js";
export * from "./receipt-cost-resolution.js";
// TRBUD-412: the shop that did the work on a bill, and a job's linked billing
// papers. Browser-safe: zod, types and labels.
export * from "./billing-links.js";
export * from "./time-clock.js";
export * from "./category-period.js";

export * from "./vendor-credit-permission.js";
export * from "./report-dashboard.js";
export * from "./vendor-credits.js";

export * from "./spend-segment.js";
// TRBUD-401: the intervals behind a downtime bar, and the other report drills.
export * from "./downtime-unit.js";
export * from "./report-drills.js";
// TRBUD-409: roadside breakdown costs — the correction body, the panel's query
// and wire shapes, and the tag's words. Browser-safe: zod, types and labels.
export * from "./roadside-costs.js";
// TRBUD-458: where a cost's reporting day came from, the audited correction of
// a posted day, and sharing a visit's labour minutes across days.
export * from "./cost-date.js";
export * from "./notifications.js";
export * from "./asset-files.js";
export * from "./purchase-order-variance.js";
// TRBUD-403: work marks ("I am working on this job now") and the labour clock.
export * from "./work-mark.js";
// TRBUD-448: the light vendor and unit lists the pickers read.
export * from "./lookups.js";
export * from "./sb-driver-contacts.js";
export * from "./integration-monitor.js";
export * from "./api-connections.js";
// TRBUD-459: PM bonus credits, the shared bonus rate and the report.
export * from "./pm-bonus.js";
// TRBUD-459: what a reopen is, for the API and the stage control.
export * from "./work-order-reopen.js";
// TRBUD-428: the maintenance-state feed TruckBuddy sends SigmaBuddy.
export * from "./sb-maintenance-state.js";
// TRBUD-466: vendor data completeness — the field keys, labels and wire shapes.
export * from "./vendor-completeness.js";
// TRBUD-467: how the receipt-reading AI performs, the "AI receipt accuracy" page.
export * from "./receipt-performance.js";
// TRBUD-466 Part 3: vendor price books, and what we paid beside the book in force.
export * from "./price-book.js";
